class ShopTheLook {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Auto attach bubbles without explicit parent_look based on block order
    this.buildAutoBubblesFromMarkers();
    // Handle individual product bubble clicks
    const productBubbles = document.querySelectorAll('.conversation-bubble--item');
    productBubbles.forEach(bubble => {
      bubble.addEventListener('click', (e) => this.handleProductBubbleClick(e));
    });

    // Handle "add all to cart" bubble clicks
    const addAllBubbles = document.querySelectorAll('.conversation-bubble--add-all');
    addAllBubbles.forEach(bubble => {
      bubble.addEventListener('click', (e) => this.handleAddAllClick(e));
    });

    // Optimize bubble positioning on window resize
    window.addEventListener('resize', this.debounce(() => this.optimizeBubblePositions(), 250));
    
    // Initial bubble position optimization
    this.optimizeBubblePositions();
  }

  buildAutoBubblesFromMarkers() {
    const markers = document.querySelectorAll('.stl-marker');
    if (!markers.length) return;
    let lastLookId = null;
    markers.forEach((m) => {
      const type = m.dataset.type;
      if (type === 'look') {
        lastLookId = m.dataset.lookId;
        return;
      }
      if (type === 'bubble') {
        const parentLook = (m.dataset.parentLook || '').trim();
        if (parentLook) return; // explicitly handled in Liquid render
        const targetLookId = parentLook || lastLookId;
        if (!targetLookId) return;
        const container = document.querySelector(`.shop-the-look__image-container[data-look="${targetLookId}"]`);
        if (!container) return;

        const bubble = document.createElement('div');
        bubble.className = 'conversation-bubble conversation-bubble--item';
        bubble.style.left = `${m.dataset.bubbleX || 0}%`;
        bubble.style.top = `${m.dataset.bubbleY || 0}%`;

        const variantId = m.dataset.variantId;
        if (variantId) {
          bubble.dataset.variantId = variantId;
          bubble.dataset.available = (m.dataset.available === 'true') ? 'true' : 'false';
          if (m.dataset.productUrl) bubble.dataset.productUrl = m.dataset.productUrl;
        } else {
          bubble.dataset.demo = 'true';
        }

        const content = document.createElement('div');
        content.className = 'bubble-content';
        const span = document.createElement('span');
        span.className = 'bubble-text';
        span.textContent = m.dataset.text || m.dataset.productTitle || '';
        content.appendChild(span);
        const arrow = document.createElement('div');
        arrow.className = 'bubble-arrow';
        bubble.appendChild(content);
        bubble.appendChild(arrow);

        // Bind click handler
        bubble.addEventListener('click', (e) => this.handleProductBubbleClick(e));

        container.appendChild(bubble);
      }
    });
    // After insertion, optimize arrow directions
    this.optimizeBubblePositions();
  }

  handleProductBubbleClick(event) {
    event.preventDefault();
    const bubble = event.currentTarget;
    const productUrl = bubble.dataset.productUrl;
    const isDemo = bubble.dataset.demo === 'true';
    
    if (isDemo) {
      // Demo mode - show alert
      const bubbleText = bubble.querySelector('.bubble-text').textContent;
      alert(`Demo: This would take you to the ${bubbleText} product page!`);
      return;
    }
    
    if (productUrl) {
      // Add click animation
      bubble.classList.add('conversation-bubble--loading');
      
      // Navigate to product page
      setTimeout(() => {
        window.location.href = productUrl;
      }, 200);
    }
  }

  async handleAddAllClick(event) {
    event.preventDefault();
    const bubble = event.currentTarget;
    // Collect variant IDs from rendered bubbles inside this look
    const container = bubble.closest('.shop-the-look__image-container');
    const bubbleNodes = container ? container.querySelectorAll('.conversation-bubble--item[data-variant-id]') : [];
    let variantIds = Array.from(bubbleNodes)
      .filter(n => (n.dataset.available || 'true') === 'true')
      .map(n => n.dataset.variantId)
      .filter(Boolean);
    
    // Fallback to data attribute (legacy)
    if (!variantIds.length && bubble.dataset.lookProducts) {
      variantIds = bubble.dataset.lookProducts.split(',').map(id => id.trim()).filter(id => id);
    }

    if (variantIds.length === 0) {
      // Nothing to add (sold out), silently exit
      return;
    }

    // Show loading state
    bubble.classList.add('conversation-bubble--loading');
    this.speedUpHeaderGradient(true);

    try {
      // Add all products to cart
      await this.addMultipleToCart(variantIds);
      
      // Immediately go to cart page after adding
      this.triggerCartUpdate();
      window.location.href = routes.cart_url;
      
    } catch (error) {
      console.error('Failed to add products to cart:', error);

      bubble.classList.remove('conversation-bubble--loading');
    }
    finally {
      this.speedUpHeaderGradient(false);
    }
  }

  async addMultipleToCart(variantIds) {
    const items = variantIds.map(id => ({
      id: parseInt(id),
      quantity: 1
    }));

    const formData = {
      items: items,
      sections: this.getCartSections()
    };

    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.message || result.description || 'Failed to add items to cart');
    }

    return result;
  }

  speedUpHeaderGradient(enable) {
    try {
      const selectors = [
        'svg.moving-rainbow-gradient-svg linearGradient#movingRainbowGradient animate',
        'svg.moving-rainbow-gradient-svg linearGradient#movingRainbowGradient animateTransform',
        'svg.moving-custom-palette-gradient-svg linearGradient#movingCustomPaletteGradient animate',
        'svg.moving-custom-palette-gradient-svg linearGradient#movingCustomPaletteGradient animateTransform'
      ];
      const nodes = document.querySelectorAll(selectors.join(','));
      nodes.forEach(node => {
        if (enable) {
          if (!node.dataset.origDur) node.dataset.origDur = node.getAttribute('dur') || '';
          const fast = '1s';
          node.setAttribute('dur', fast);
        } else if (node.dataset.origDur) {
          node.setAttribute('dur', node.dataset.origDur);
        }
      });
    } catch (e) {
      // no-op
    }
  }

  getCartSections() {
    // Get sections that need to be updated when cart changes
    return [
      'cart-drawer',
      'cart-icon-bubble',
      'cart-live-region-text',
      'cart-notification'
    ].join(',');
  }

  triggerCartUpdate() {
    // Trigger cart update events for other components
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    // Publish to PubSub if available (Dawn theme pattern)
    if (window.publish && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate);
    }
  }

  optimizeBubblePositions() {
    // Simplified iOS-like tails:
    // - Blue bubbles (product) => bottom-right tail
    // - Gray bubbles (add-all) => bottom-left tail
    const bubbles = document.querySelectorAll('.conversation-bubble');
    
    bubbles.forEach(bubble => {
      bubble.classList.remove('conversation-bubble--top', 'conversation-bubble--left', 'conversation-bubble--right', 'conversation-bubble--bottom');
      const isAddAll = bubble.classList.contains('conversation-bubble--add-all');
      bubble.classList.add('conversation-bubble--bottom');
      bubble.classList.add(isAddAll ? 'conversation-bubble--left' : 'conversation-bubble--right');

      // Ensure a real tail element exists as a sibling of .bubble-content
      // This keeps the tail behind the text/background and prevents overlap
      const content = bubble.querySelector('.bubble-content');
      if (content) {
        let tail = bubble.querySelector('.bubble-tail');
        if (!tail) {
          tail = document.createElement('span');
          tail.className = 'bubble-tail';
          bubble.appendChild(tail);
        } else if (tail.parentElement !== bubble) {
          bubble.appendChild(tail);
        }
        // Precisely match bubble background color to avoid subtle mismatches
        try {
          const bg = window.getComputedStyle(content).backgroundColor;
          if (bg) tail.style.background = bg;
        } catch (e) { /* no-op */ }
      }
    });
  }


  // Utility function for debouncing resize events
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ShopTheLook();
});

// Also initialize if DOM is already loaded
if (document.readyState !== 'loading') {
  new ShopTheLook();
}
